import { motion } from 'framer-motion'
import { ArrowRightIcon, CheckCircleIcon, ChartBarIcon, ClockIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import styles from './Casos.module.css'

interface CaseStudy {
  id: number
  title: string
  category: string
  description: string
  impact: string[]
  technologies: string[]
  color: string
  icon: React.ComponentType<{ className?: string }>
}

const cases: CaseStudy[] = [
  {
    id: 1,
    title: "Transformando ideas en realidades digitales",
    category: "Desarrollo Móvil",
    description: "Un grupo de emprendedores visionarios requería una solución móvil para llegar a un mercado joven y dinámico. Nuestro equipo llevó a cabo un proceso integral de análisis, diseño y desarrollo de una aplicación móvil multiplataforma para Android e iOS. La solución no solo conectó a los usuarios con el servicio, sino que también abrió nuevas oportunidades de monetización y escalabilidad. Hoy, esta aplicación es el centro de la estrategia de crecimiento de la startup, marcando un antes y un después en su posicionamiento.",
    impact: ["Multiplicó oportunidades de monetización", "Centro de estrategia de crecimiento", "Posicionamiento transformado"],
    technologies: ["React Native", "Firebase", "Analytics", "Push Notifications"],
    color: "#3b82f6, #6366f1",
    icon: ChartBarIcon
  },
  {
    id: 2,
    title: "Eficiencia documental en organizaciones de alto impacto",
    category: "Gestión Digital",
    description: "Varias organizaciones privadas y entidades públicas enfrentaban un problema común: la dispersión, pérdida y duplicidad de documentos. Mediante la implementación de sistemas de gestión electrónica de archivos, logramos que estas instituciones migraran hacia procesos totalmente digitales, eliminando cuellos de botella operativos y garantizando la trazabilidad y seguridad de la información.",
    impact: ["60% reducción en tiempos administrativos", "Cumplimiento normativo impecable", "Eliminación de duplicidades"],
    technologies: ["ECM Systems", "OCR", "Blockchain", "Cloud Storage"],
    color: "#10b981, #059669",
    icon: CheckCircleIcon
  },
  {
    id: 3,
    title: "Automatización inteligente de procesos documentales",
    category: "Automatización",
    description: "Instituciones públicas con miles de trámites diarios necesitaban optimizar sus tiempos de respuesta. Se diseñaron e implementaron sistemas de automatización documental, integrando workflows digitales y herramientas inteligentes de control. La consecuencia fue un impacto directo en la calidad del servicio ciudadano, con trámites que antes tardaban semanas y que ahora se resuelven en horas.",
    impact: ["De semanas a horas en trámites", "Miles de procesos optimizados", "Mejora en servicio ciudadano"],
    technologies: ["BPM", "RPA", "AI Workflows", "API Integration"],
    color: "#f59e0b, #d97706",
    icon: ClockIcon
  },
  {
    id: 4,
    title: "Creación de expedientes digitales y preservación histórica",
    category: "Digitalización",
    description: "En proyectos estratégicos, acompañamos a instituciones en la digitalización masiva de documentos físicos y su indexación en expedientes electrónicos. Esta transición permitió preservar información histórica invaluable y garantizar el acceso en tiempo real a archivos antes confinados en bodegas físicas. La transformación no solo mejoró la gestión administrativa, sino que también aportó al desarrollo de políticas de transparencia y gobierno digital.",
    impact: ["Preservación histórica digital", "Acceso en tiempo real", "Transparencia gubernamental"],
    technologies: ["OCR Advanced", "Digital Archives", "Indexing AI", "Metadata Management"],
    color: "#8b5cf6, #7c3aed",
    icon: CheckCircleIcon
  },
  {
    id: 5,
    title: "Webs transaccionales y dinámicas para la nueva economía",
    category: "Desarrollo Web",
    description: "Empresas privadas y entidades públicas necesitaban presencia digital de alto nivel. Diseñamos y desarrollamos páginas web transaccionales, interactivas y dinámicas, optimizadas para generar confianza y facilitar procesos en línea. Gracias a estas soluciones, los clientes expandieron su mercado, redujeron costos operativos y multiplicaron su capacidad de atención digital.",
    impact: ["Expansión de mercado", "Reducción de costos operativos", "Multiplicación de atención digital"],
    technologies: ["React", "Node.js", "Payment Gateways", "Progressive Web Apps"],
    color: "#06b6d4, #0891b2",
    icon: ChartBarIcon
  },
  {
    id: 6,
    title: "Innovación tecnológica en el sector deportivo",
    category: "Software Especializado",
    description: "En el ámbito del deporte, se desarrollaron soluciones de software a la medida que integran análisis de desempeño, gestión administrativa y comunicación con aficionados. Estas plataformas no solo profesionalizaron la gestión de equipos y ligas, sino que también potenciaron el rendimiento deportivo mediante datos y métricas en tiempo real.",
    impact: ["Profesionalización de gestión", "Potenciación del rendimiento", "Métricas en tiempo real"],
    technologies: ["Sports Analytics", "Real-time Data", "Mobile Apps", "IoT Sensors"],
    color: "#22c55e, #16a34a",
    icon: ChartBarIcon
  },
  {
    id: 7,
    title: "Ciberseguridad y confianza digital",
    category: "Seguridad",
    description: "A través de servicios de análisis forense digital y hacking ético, ayudamos a organizaciones a detectar vulnerabilidades críticas y fortalecer su infraestructura tecnológica. En varios casos, se identificaron brechas de seguridad antes de que fueran explotadas, protegiendo millones en activos digitales y reforzando la confianza de usuarios y clientes.",
    impact: ["Vulnerabilidades detectadas preventivamente", "Millones en activos protegidos", "Confianza reforzada"],
    technologies: ["Penetration Testing", "Forensic Analysis", "Security Audits", "Threat Intelligence"],
    color: "#ef4444, #dc2626",
    icon: ShieldCheckIcon
  },
  {
    id: 8,
    title: "Potenciando la inteligencia de los datos",
    category: "Big Data & Analytics",
    description: "Con el desarrollo de proyectos de analítica avanzada y big data, instituciones lograron transformar datos dispersos en información estratégica para la toma de decisiones. Estos sistemas aportaron claridad en escenarios de negocio, investigación y gestión pública, abriendo nuevas rutas de eficiencia y competitividad.",
    impact: ["Datos dispersos transformados", "Información estratégica", "Nuevas rutas de eficiencia"],
    technologies: ["Big Data", "Machine Learning", "Data Visualization", "Predictive Analytics"],
    color: "#6366f1, #4f46e5",
    icon: ChartBarIcon
  },
  {
    id: 9,
    title: "Inteligencia Artificial aplicada a la gestión documental",
    category: "Inteligencia Artificial",
    description: "En uno de los retos más ambiciosos, se integraron modelos de Inteligencia Artificial en sistemas documentales, permitiendo clasificar, priorizar y procesar información con mínima intervención humana. Esta innovación no solo elevó la productividad de las áreas administrativas, sino que sentó las bases para el futuro de la gestión documental inteligente.",
    impact: ["Procesamiento con mínima intervención", "Productividad elevada", "Bases para futuro inteligente"],
    technologies: ["NLP", "Computer Vision", "Machine Learning", "Document AI"],
    color: "#a855f7, #9333ea",
    icon: ChartBarIcon
  }
]

export default function Casos() {
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.headerSection}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={styles.headerContent}
        >
          <h1 className={styles.mainTitle}>
            Historias y Casos de Éxito
          </h1>
          <p className={styles.mainDescription}>
            Descubre cómo hemos transformado ideas en realidades digitales, ayudando a organizaciones 
            de todos los sectores a alcanzar sus objetivos tecnológicos y superar sus desafíos más complejos.
          </p>
        </motion.div>
      </div>

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.statsContainer}>
          <div className={styles.statsGrid}>
            {[
              { number: "150+", label: "Proyectos Completados" },
              { number: "98%", label: "Satisfacción del Cliente" },
              { number: "60%", label: "Promedio de Eficiencia Ganada" },
              { number: "24/7", label: "Soporte Técnico" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={styles.statCard}
              >
                <div className={styles.statNumber}>
                  {stat.number}
                </div>
                <div className={styles.statLabel}>{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases Grid */}
      <section className={styles.casesSection}>
        <div className={styles.casesContainer}>
          {cases.map((caseStudy, index) => {
            const IconComponent = caseStudy.icon
            return (
              <motion.div
                key={caseStudy.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -12 }}
                className={styles.caseCard}
                style={{
                  '--case-gradient': `linear-gradient(135deg, ${caseStudy.color})`
                } as React.CSSProperties}
              >
                {/* Content */}
                <div className={styles.caseContent}>
                  <div className={styles.caseGrid}>
                    {/* Left Content */}
                    <div className={styles.caseMainContent}>
                      {/* Header */}
                      <div className={styles.caseHeader}>
                        <motion.div 
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          className={styles.caseIconContainer}
                          style={{
                            background: `linear-gradient(135deg, ${caseStudy.color})`
                          }}
                        >
                          <IconComponent className={styles.caseIcon} />
                        </motion.div>
                        <div className={styles.caseHeaderText}>
                          <motion.div 
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className={styles.caseBadge}
                          >
                            {caseStudy.category}
                          </motion.div>
                          <motion.h3 
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className={styles.caseTitle}
                          >
                            {caseStudy.title}
                          </motion.h3>
                        </div>
                      </div>
                      
                      {/* Description */}
                      <motion.p 
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className={styles.caseDescription}
                      >
                        {caseStudy.description}
                      </motion.p>

                      {/* Impact Section */}
                      <motion.div 
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className={styles.impactSection}
                      >
                        <h4 className={styles.impactTitle}>
                          <div className={styles.impactIndicator} />
                          Impacto Logrado
                        </h4>
                        <div className={styles.impactList}>
                          {caseStudy.impact.map((impact, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, x: -30 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.7 + idx * 0.1 }}
                              className={styles.impactItem}
                            >
                              <CheckCircleIcon className={styles.impactIcon} />
                              <span className={styles.impactText}>
                                {impact}
                              </span>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>

                      {/* Technologies */}
                      <motion.div 
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.8 }}
                        className={styles.techSection}
                      >
                        <h4 className={styles.techTitle}>
                          <div className={styles.techIndicator} />
                          Tecnologías Utilizadas
                        </h4>
                        <div className={styles.techList}>
                          {caseStudy.technologies.map((tech, idx) => (
                            <motion.span
                              key={idx}
                              initial={{ opacity: 0, scale: 0.8 }}
                              whileInView={{ opacity: 1, scale: 1 }}
                              whileHover={{ scale: 1.05 }}
                              transition={{ delay: 0.9 + idx * 0.05 }}
                              className={styles.techItem}
                            >
                              {tech}
                            </motion.span>
                          ))}
                        </div>
                      </motion.div>
                    </div>

                    {/* Right Visual Element */}
                    <div className={styles.caseVisual}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        whileHover={{ scale: 1.1 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className={styles.visualElement}
                      >
                        <div 
                          className={styles.numberCircle}
                          style={{
                            background: `linear-gradient(135deg, ${caseStudy.color})`
                          }}
                        >
                          {/* Number */}
                          <div className={styles.caseNumber}>
                            {String(caseStudy.id).padStart(2, '0')}
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContainer}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={styles.ctaCard}
          >
            <h2 className={styles.ctaTitle}>
              ¿Listo para escribir tu historia de éxito?
            </h2>
            <p className={styles.ctaDescription}>
              Únete a las organizaciones que han transformado sus operaciones con nuestras soluciones tecnológicas innovadoras.
            </p>
            <div className={styles.ctaButtons}>
              <Link to="/contacto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={styles.primaryButton}
                >
                  Comenzar mi proyecto
                  <ArrowRightIcon className={styles.buttonIcon} />
                </motion.button>
              </Link>
              <Link to="/servicios">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={styles.secondaryButton}
                >
                  Ver nuestros servicios
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
